import type { IdentityCandidateSources } from './buildActiveFormDescriptor';
import type { IdentityCandidate } from './types';

/**
 * The minimal shapes this module needs from the directory and vocabulary
 * queries the form's own pickers already run (`useGetUsers`,
 * `useGetUserGroups`, `useTagOptions`, `useDepartmentOptions`).
 *
 * Declared STRUCTURALLY rather than importing the generated GraphQL types, so
 * this stays a pure, provider-free module the unit tests can drive directly —
 * the same reason `resolveCustomAttributeOptionFields` declares its own render
 * prop shape.
 */
export interface DirectoryUser {
  Id?: string | null;
  FriendlyName?: string | null;
  /**
   * Org membership status — `'active'` or `'archived'`, or `null` when the
   * membership row is missing (`useGetUsers` maps it as such).
   */
  Status?: string | null;
  /** RiskSmart support accounts, hidden from customers' own pickers. */
  IsCustomerSupport?: boolean | null;
}

export interface DirectoryUserGroup {
  Id: string;
  Name?: string | null;
  /**
   * Whether the group may hold owner/contributor roles. The pickers' default
   * `groupFilter` is exactly this flag, so a group without it is never offered.
   */
  OwnerContributor?: boolean | null;
}

export interface DirectoryTagType {
  TagTypeId: string;
  Name?: string | null;
}

export interface DirectoryDepartmentType {
  DepartmentTypeId: string;
  Name?: string | null;
}

export interface IdentityDirectory {
  users: DirectoryUser[];
  userGroups: DirectoryUserGroup[];
  tagTypes: DirectoryTagType[];
  departmentTypes: DirectoryDepartmentType[];
  /**
   * Whether the signed-in user is themselves a RiskSmart support account. Drives
   * the `IsCustomerSupport` exclusion below, mirroring `userToOption`'s
   * `hideAsCustomerSupport`.
   */
  isCustomerSupport: boolean;
}

/**
 * A candidate needs a usable id AND a usable name. A row missing either cannot
 * be resolved from a name or written to the form, so it is dropped rather than
 * published as a candidate with an empty label — which would otherwise make
 * every unnamed row collide with every other, turning a blank proposal into an
 * `identity_ambiguous` rejection instead of a clean miss.
 */
const toCandidate = (
  id: string | null | undefined,
  name: string | null | undefined,
  kind?: IdentityCandidate['kind']
): IdentityCandidate[] => {
  const label = (name ?? '').trim();
  if (!id || label === '') {
    return [];
  }

  return [{ id, label, ...(kind ? { kind } : {}) }];
};

/**
 * Build the identity vocabularies `buildActiveFormDescriptor` resolves names
 * against (AID-394).
 *
 * Every exclusion below mirrors what the field's own picker offers, because that
 * is what makes the candidate set defensible: prefill should be able to propose
 * exactly the people, groups, tags and departments the user could have picked by
 * hand, and nothing else. Concretely:
 *
 *  - Only `'active'` users are kept. Tested POSITIVELY, mirroring the picker's
 *    own `Status === 'active'` grouping rather than excluding `'archived'`: the
 *    status can also be `null` (a missing org-membership row), and such a user
 *    appears in NEITHER of the picker's groups, so a negative test would have
 *    made this set strictly larger than what the user can actually pick. Same
 *    reasoning covers an unexpected casing or a future third status — unknown
 *    means not proposable.
 *
 *    A never-signed-in user is deliberately still a candidate. The pickers can
 *    hide those, but only behind a per-user display preference, and a newly
 *    invited colleague is a perfectly legitimate owner.
 *  - CUSTOMER-SUPPORT users are dropped unless the viewer is one themselves, as
 *    `userToOption`'s `hideAsCustomerSupport` does. Proposing a RiskSmart
 *    support account as a customer's risk owner is a failure that looks
 *    authoritative on the card.
 *  - GROUPS that are not `OwnerContributor` are dropped, matching the pickers'
 *    default `groupFilter`.
 *
 * Users and groups share ONE `users` vocabulary, discriminated by `kind`, so a
 * name belonging to both a person and a group is detectable as ambiguous rather
 * than resolving differently depending on which list was searched first.
 */
export const resolveIdentityCandidates = ({
  users,
  userGroups,
  tagTypes,
  departmentTypes,
  isCustomerSupport,
}: IdentityDirectory): IdentityCandidateSources => ({
  users: [
    ...users
      .filter((user) => user.Status === 'active')
      .filter((user) => !user.IsCustomerSupport || isCustomerSupport)
      .flatMap((user) => toCandidate(user.Id, user.FriendlyName, 'user')),
    ...userGroups
      .filter((group) => group.OwnerContributor)
      .flatMap((group) => toCandidate(group.Id, group.Name, 'userGroup')),
  ],
  tags: tagTypes.flatMap((tag) => toCandidate(tag.TagTypeId, tag.Name)),
  departments: departmentTypes.flatMap((department) =>
    toCandidate(department.DepartmentTypeId, department.Name)
  ),
});
